import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { 
  Bot, Send, Loader2, Sparkles, Image as ImageIcon, 
  Lightbulb, MessageSquare, X, Download, Copy, Check,
  Wand2, RefreshCw
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chat Message Component
const ChatMessage = ({ message, isBot }) => (
  <div className={cn(
    "flex gap-3 mb-4",
    isBot ? "flex-row" : "flex-row-reverse"
  )}>
    <div className={cn(
      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
      isBot ? "bg-pink-500/20" : "bg-secondary"
    )}>
      {isBot ? <Bot className="w-4 h-4 text-pink-500" /> : <span className="text-xs">You</span>}
    </div>
    <div className={cn(
      "max-w-[80%] rounded-2xl px-4 py-2",
      isBot ? "bg-secondary/50" : "bg-pink-500 text-white"
    )}>
      <p className="text-sm whitespace-pre-wrap">{message}</p>
    </div>
  </div>
);

// Prompt Card Component  
const PromptCard = ({ prompt, onUse, onCopy }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="bg-secondary/30 rounded-xl p-4 space-y-3">
      <p className="text-sm">{prompt}</p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={handleCopy}>
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button size="sm" onClick={() => onUse(prompt)}>
          <Wand2 className="w-3 h-3 mr-1" />
          Generate
        </Button>
      </div>
    </div>
  );
};

// Main AI Bot Component
export default function AIBotModal({ open, onClose }) {
  const { token, language } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');
  const [sessionId] = useState(`session-${Date.now()}`);
  
  // Chat state
  const [messages, setMessages] = useState([
    { role: 'bot', content: language === 'ht' 
      ? 'Bonjou! Mwen se FityBot. Mwen ka ede w devlope ide, kreye pwompt pou imaj, oswa reponn kesyon ou yo. Kisa ou ta renmen fè jodi a?' 
      : 'Hello! I\'m FityBot. I can help you develop ideas, create image prompts, or answer your questions. What would you like to do today?'
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  // Prompt state
  const [ideaInput, setIdeaInput] = useState('');
  const [styleInput, setStyleInput] = useState('');
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [promptExplanation, setPromptExplanation] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  
  // Image state
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  
  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Send chat message
  const sendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        session_id: sessionId,
        mode: 'general',
        history: messages.map(m => ({ role: m.role === 'bot' ? 'assistant' : 'user', content: m.content }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, { role: 'bot', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(language === 'ht' ? 'Erè nan chat' : 'Chat error');
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: language === 'ht' ? 'Eskize m, mwen gen yon pwoblèm. Tanpri eseye ankò.' : 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setChatLoading(false);
    }
  };
  
  // Generate prompts from idea
  const generatePrompts = async () => {
    if (!ideaInput.trim() || promptLoading) return;
    
    setPromptLoading(true);
    setSuggestedPrompts([]);
    setPromptExplanation('');
    
    try {
      const response = await axios.post(`${API}/ai/suggest-prompts`, {
        idea: ideaInput.trim(),
        style: styleInput.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuggestedPrompts(response.data.prompts);
      setPromptExplanation(response.data.explanation);
    } catch (error) {
      console.error('Prompt generation error:', error);
      toast.error(language === 'ht' ? 'Erè nan kreye pwompt' : 'Prompt generation error');
    } finally {
      setPromptLoading(false);
    }
  };
  
  // Generate image from prompt
  const generateImage = async (prompt = imagePrompt) => {
    if (!prompt.trim() || imageLoading) return;
    
    setImageLoading(true);
    setGeneratedImage(null);
    
    try {
      const response = await axios.post(`${API}/ai/generate-image`, {
        prompt: prompt.trim(),
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minute timeout for image generation
      });
      
      setGeneratedImage(response.data.image_base64);
      toast.success(language === 'ht' ? 'Imaj kreye!' : 'Image generated!');
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error(language === 'ht' ? 'Erè nan kreye imaj' : 'Image generation error');
    } finally {
      setImageLoading(false);
    }
  };
  
  // Download generated image
  const downloadImage = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${generatedImage}`;
    link.download = `fityh-ai-${Date.now()}.png`;
    link.click();
  };
  
  // Use prompt for image generation
  const usePromptForImage = (prompt) => {
    setImagePrompt(prompt);
    setActiveTab('image');
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            FityBot AI
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="chat" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Chat
            </TabsTrigger>
            <TabsTrigger value="prompts" className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              {language === 'ht' ? 'Pwompt' : 'Prompts'}
            </TabsTrigger>
            <TabsTrigger value="image" className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              {language === 'ht' ? 'Imaj' : 'Image'}
            </TabsTrigger>
          </TabsList>
          
          {/* Chat Tab */}
          <TabsContent value="chat" className="flex-1 flex flex-col min-h-0 mt-4">
            <div className="flex-1 overflow-y-auto pr-2 min-h-[300px] max-h-[400px]">
              {messages.map((msg, i) => (
                <ChatMessage key={i} message={msg.content} isBot={msg.role === 'bot'} />
              ))}
              {chatLoading && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 text-pink-500 animate-spin" />
                  </div>
                  <div className="bg-secondary/50 rounded-2xl px-4 py-2">
                    <p className="text-sm text-muted-foreground">
                      {language === 'ht' ? 'Ap reflechi...' : 'Thinking...'}
                    </p>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={language === 'ht' ? 'Ekri mesaj ou...' : 'Type your message...'}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                disabled={chatLoading}
                data-testid="ai-chat-input"
              />
              <Button onClick={sendMessage} disabled={chatLoading || !chatInput.trim()}>
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </TabsContent>
          
          {/* Prompts Tab */}
          <TabsContent value="prompts" className="flex-1 flex flex-col min-h-0 mt-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {language === 'ht' ? 'Ide ou' : 'Your Idea'}
                </label>
                <Textarea
                  value={ideaInput}
                  onChange={(e) => setIdeaInput(e.target.value)}
                  placeholder={language === 'ht' 
                    ? 'Dekri ide ou... (egzanp: yon chat sou yon planèt espas)' 
                    : 'Describe your idea... (e.g., a cat on a space planet)'}
                  rows={2}
                  data-testid="ai-idea-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {language === 'ht' ? 'Estil (opsyonèl)' : 'Style (optional)'}
                </label>
                <Input
                  value={styleInput}
                  onChange={(e) => setStyleInput(e.target.value)}
                  placeholder={language === 'ht' 
                    ? 'egzanp: anime, realistic, watercolor...' 
                    : 'e.g., anime, realistic, watercolor...'}
                />
              </div>
              
              <Button 
                onClick={generatePrompts} 
                disabled={promptLoading || !ideaInput.trim()}
                className="w-full"
                data-testid="generate-prompts-btn"
              >
                {promptLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {language === 'ht' ? 'Kreye Pwompt' : 'Generate Prompts'}
              </Button>
            </div>
            
            {suggestedPrompts.length > 0 && (
              <div className="flex-1 overflow-y-auto mt-4 space-y-3 max-h-[250px]">
                {promptExplanation && (
                  <p className="text-sm text-muted-foreground italic">{promptExplanation}</p>
                )}
                {suggestedPrompts.map((prompt, i) => (
                  <PromptCard 
                    key={i} 
                    prompt={prompt} 
                    onUse={usePromptForImage}
                    onCopy={() => navigator.clipboard.writeText(prompt)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Image Tab */}
          <TabsContent value="image" className="flex-1 flex flex-col min-h-0 mt-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {language === 'ht' ? 'Pwompt pou imaj' : 'Image Prompt'}
                </label>
                <Textarea
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder={language === 'ht' 
                    ? 'Dekri imaj ou vle kreye an detay...' 
                    : 'Describe the image you want to create in detail...'}
                  rows={3}
                  data-testid="ai-image-prompt"
                />
              </div>
              
              <Button 
                onClick={() => generateImage()} 
                disabled={imageLoading || !imagePrompt.trim()}
                className="w-full"
                data-testid="generate-image-btn"
              >
                {imageLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {language === 'ht' ? 'Ap kreye... (ka pran 1 minit)' : 'Creating... (may take 1 min)'}
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    {language === 'ht' ? 'Kreye Imaj' : 'Generate Image'}
                  </>
                )}
              </Button>
            </div>
            
            {generatedImage && (
              <div className="flex-1 mt-4 space-y-3">
                <div className="relative rounded-xl overflow-hidden bg-secondary/30">
                  <img 
                    src={`data:image/png;base64,${generatedImage}`}
                    alt="AI Generated"
                    className="w-full h-auto max-h-[300px] object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={downloadImage} variant="outline" className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    {language === 'ht' ? 'Telechaje' : 'Download'}
                  </Button>
                  <Button onClick={() => generateImage()} variant="outline" className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {language === 'ht' ? 'Reeseye' : 'Regenerate'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
